<template>
  <v-container fluid>
    <v-col class="col">
      <v-slide-y-transition mode="out-in">
        <v-row class="child-flex">
          <v-card>
            <v-card-text>
              <v-card-title>
                <h1 class="text-h4 mr-2">CSV Datasources</h1>
                <refresh-spinner v-model="isRefreshingData" @click="refreshList"></refresh-spinner>
                <v-spacer></v-spacer>
                <v-text-field
                  @keydown.native.escape="search=''"
                  label="Search"
                  single-line
                  hide-details
                  clearable
                  v-model="search"
                ></v-text-field>
              </v-card-title>
            </v-card-text>
            <v-data-table
              :headers="tableColumns"
              :items="queriedData"
              item-key="id"
              class="elevation-1"
            >
            <!--
              <template v-slot:item.type="{ item }">
                <v-chip :color="dsColor(item)" text-color="white">{{ dsLabel(item) }}</v-chip>
              </template>
            -->
              <template v-slot:item.status="{ item }">
                <v-chip v-if="item.status" :color="dsStatusColor(item)" text-color="white">{{ item.status }}</v-chip>
              </template>
              <template v-slot:item.meta="{ item }">
                <div class="constrainWidth">
                  <template v-if="isComplete(item)">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-chip v-on="on" color="cyan" text-color="white">
                          <v-avatar color="cyan darken-2">
                            <v-icon small>mdi-format-list-bulleted</v-icon>
                          </v-avatar>
                          {{ item.meta.row_count }}
                        </v-chip>
                      </template>
                      <span>{{ item.meta.row_count }} Rows</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-chip v-on="on" color="cyan" text-color="white">
                          <v-avatar color="cyan darken-2">
                            <v-icon small>mdi-format-columns</v-icon>
                          </v-avatar>
                          {{ item.meta.headers.length }}
                        </v-chip>
                      </template>
                      <span>{{ item.meta.headers.length }} Columns</span>
                    </v-tooltip>
                  </template>
                  <template v-if="isError(item)">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <div
                          v-on="on"
                          class="text-truncate"
                          style="color:red"
                        >{{ item.meta.errors }}</div>
                      </template>
                      <span>{{ item.meta.errors }}</span>
                    </v-tooltip>
                  </template>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip
                  top
                  v-for="(rowAction, index) in datasourceActions(item)"
                  :key="index"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      medium
                      class="mr-2"
                      @click="executeAction(rowAction.action, item)"
                    >{{rowAction.icon}}</v-icon>
                  </template>
                  <span>{{rowAction.title}}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-row>
      </v-slide-y-transition>
    </v-col>
    <v-btn fab bottom right color="pink" dark fixed :to="{'name': 'new_csv_datasource_path'}">
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import RefreshSpinner from '@/components/ui/RefreshSpinner'
import { pathOr, curry, __ } from 'ramda'
import { DatasourceApi } from '@/services'

export default {
  components: {
    [RefreshSpinner.name]: RefreshSpinner
  },
  computed: {
    ...mapState('DatasourceStore', { tableData: 'datasources' }),
    ...mapState('ApplicationStore', ['dealerScopedView']),
    ...mapState('DealerStore', ['dealer']),
    queriedData () {
      if (!this.search) {
        return this.tableData
      }
      let result = this.tableData.filter(row => {
        let isIncluded = false
        for (let key of this.propsToSearch) {
          let rowValue = pathOr('', key.split('.'), row).toString()
          if (
            rowValue.includes &&
            rowValue.toLowerCase().includes(this.search.toLowerCase())
          ) {
            isIncluded = true
          }
        }
        return isIncluded
      })
      return result
    }
  },
  data: function () {
    return {
      isRefreshingData: false,
      search: '',
      propsToSearch: ['name', 'dealer.name'],
      tableColumns: [
        { text: 'Name', value: 'name' },
        // { text: 'Type', value: 'type' },
        { text: 'Status', value: 'status' },
        { text: 'Dealer', value: 'dealer.name' },
        { text: 'Info', value: 'meta' },
        { text: 'Actions', value: 'actions' }
      ],
      datasourceTypeSettings: {
        CsvDatasource: { label: 'csv', color: 'blue-grey' },
        DmsDatasource: { label: 'dms', color: 'brown' }
      },
      datasourceStatusSettings: {
        error: { color: 'red' },
        processing: { color: 'orange' },
        complete: { color: 'green' }
      },
      actions: {
        complete: [
          { title: 'Show', action: 'detailsView', icon: 'mdi-binoculars' },
          { title: 'Download', action: 'download', icon: 'mdi-download' },
          { title: 'Delete', action: 'handleDelete', icon: 'mdi-delete' }
        ],
        processing: [
          { title: 'Show', action: 'detailsView', icon: 'mdi-binoculars' }
        ],
        error: [
          { title: 'Show', action: 'detailsView', icon: 'mdi-binoculars' },
          { title: 'Delete', action: 'handleDelete', icon: 'mdi-delete' }
        ]
      }
    }
  },
  mounted: function () {
    this.refreshList()
  },
  watch: {
    dealer: function (newDealer, oldDealer) {
      this.refreshList()
    }
  },
  methods: {
    refreshList () {
      this.isRefreshingData = true
      const queryParams = this.dealerScopedView ? { dealer_id: this.dealer.id } : {}
      return this.$store
        .dispatch('DatasourceStore/fetchCsvDatasources', queryParams)
        .then(() => {
          this.isRefreshingData = false
        })
        .catch(error => {
          this.isRefreshingData = false
          this.$flash({
            showClose: true,
            message:
              'Error Loading Datasources. Reload to try again. ' +
              error.message,
            type: 'error'
          })
        })
    },
    handleDelete (row) {
      const shouldDelete = confirm('Are you sure you want to delete? This action can not be undone.')
      if (shouldDelete) {
        this.$store.dispatch('DatasourceStore/deleteDatasource', row)
          .then(() => {
            this.refreshList()
            this.$flash({
              showClose: true,
              message: 'Deleted Datasource',
              type: 'mini',
              duration: 2000
            })
          })
          .catch(error => {
            this.$flash({
              showClose: true,
              message: 'Error Deleting Datasource. ' + error.message,
              type: 'error',
              duration: 0
            })
          })
      }
    },
    handleEdit (item) {
      this.$router.push({
        name: 'edit_datasource_path',
        params: { id: item.id }
      })
    },
    dsLabel (item) {
      return this.datasourceTypeSettings[item.type].label
    },
    dsColor (item, extraClass = '') {
      return this.datasourceTypeSettings[item.type].color + ' ' + extraClass
    },
    dsStatusColor (item) {
      return this.datasourceStatusSettings[item.status].color
    },
    isComplete (item) {
      return item.status === 'complete'
    },
    isError (item) {
      return item.status === 'error'
    },
    executeAction (action, item) {
      if (typeof (action) === 'function') {
        action(item)
      } else {
        this[action](item)
      }
    },
    datasourceActions (item) {
      const baseActions = this.actions[item.status]
      if (!this.isComplete(item)) {
        return baseActions
      } else {
        const addlDocs = (item.params.additional_docs || []).map(doc => {
          return { title: doc.description, action: curry(this.downloadAdditional)(__, doc.doc_id), icon: this.documentIcon(doc.type) }
        })
        return baseActions.concat(addlDocs)
      }
    },
    async _downloadUrl (actionName, item, optionalDocId) {
      try {
        const url = await DatasourceApi[actionName](item, optionalDocId)
        const downloadLink = document.createElement('a')
        downloadLink.setAttribute('href', url)
        downloadLink.setAttribute('style', 'display:none')
        downloadLink.setAttribute('target', '_blank')
        document.body.append(downloadLink)

        setTimeout(function () {
          downloadLink.click()
          downloadLink.remove()
        }, null)
      } catch (error) {
        this.$flash({
          showClose: true,
          message: 'Error downloading CSV File! ' + error.message,
          type: 'error'
        })
      }
    },
    async download (item) {
      await this._downloadUrl('getCsvFinalDocumentUrl', item)
    },
    async downloadOriginal (item) {
      await this._downloadUrl('getCsvOriginalDocumentUrl', item)
    },
    async downloadAdditional (item, docId) {
      await this._downloadUrl('getCsvAdditionalDocumentUrl', item, docId)
    },
    detailsView (item) {
      this.$router.push({
        name: 'show_csv_datasource_path',
        params: { id: item.id }
      })
    },
    documentIcon (docType) {
      return {
        excel: 'mdi-microsoft-excel',
        xlsx: 'mdi-microsoft-excel'
      }[docType]
    }
  }
}
</script>

<style>
.constrainWidth {
  max-width: 200px !important;
}
</style>
